import {
  Controller,
  useFormContext,
  type ControllerRenderProps,
  type FieldValues,
} from "react-hook-form";
import { TextInput, REQUIRED_ERROR_MESSAGE } from "@SeemplicityDev/shared";
import React, { useEffect } from "react";
import { useQuery } from "@apollo/client";
import { GET_PARTNER_REMEDIATION_QUEUE } from "../TicketingAPI";
import "./CustomPolicyPreferences.scss";

interface CustomPolicyPreferencesProps {
  policyId?: string | number;
}

interface PartnerRemediationQueueData {
  partner_remediation_queue?: {
    queue_size?: number | null;
  } | null;
}

const CustomPolicyPreferences: React.FC<CustomPolicyPreferencesProps> = ({
  policyId,
}) => {
  const {
    formState: { errors },
    control,
    resetField,
  } = useFormContext();

  const { data: partnerRemediationQueueData } =
    useQuery<PartnerRemediationQueueData>(GET_PARTNER_REMEDIATION_QUEUE, {
      variables: {
        partner_external_id: policyId?.toString(),
      },
      skip: !policyId,
    });

  useEffect(() => {
    if (!!policyId && !!partnerRemediationQueueData) {
      resetField("queueSize", {
        defaultValue:
          partnerRemediationQueueData?.partner_remediation_queue?.queue_size?.toString() ||
          "10",
      });
    }
  }, [policyId, partnerRemediationQueueData]);

  return (
    <Controller
      name={`queueSize`}
      render={({
        field: { value, onChange, onBlur, ref },
      }: {
        field: ControllerRenderProps<FieldValues, "queueSize">;
      }) => {
        return (
          <>
            <div>
              <label className="input-label">Queue Size</label>
              <div className="input-description">
                Maximum number of open tickets at a given time per policy.
                Additional CVEs are added to the waiting list (see AVM {">"}
                Ticketing).
                <br />
                We recommend maintaining a queue size of up to 10 tickets.
              </div>
            </div>
            <div className={"queue-size-input-holder"}>
              <TextInput
                data-testid={"queue-size"}
                onChange={onChange}
                inputRef={ref}
                onBlur={onBlur}
                value={value}
                error={errors?.queueSize}
                min={1}
                max={50}
                type={"number"}
                size="xxs"
                className="rmq-input-que-size"
                // Shared TextInput types `labelRight` as a string; the original
                // JS passed the boolean shorthand as a truthy toggle.
                labelRight={true as unknown as string}
              />
              <label className="input-label">Tickets</label>
            </div>
          </>
        );
      }}
      control={control}
      defaultValue={"10"}
      rules={{
        required: REQUIRED_ERROR_MESSAGE,
        pattern: {
          value: /^[1-9][0-9]*$/,
          message: "Invalid Queue Size",
        },
        max: {
          value: 50,
          message: "Queue size cannot be greater than 50.",
        },
      }}
    />
  );
};

export default CustomPolicyPreferences;
